/**
 * Google Analytics 4 (GA4) Utility
 *
 * Handles GA4 initialization and event tracking using gtag.js
 */

// Extend Window interface to include gtag and dataLayer
declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

/**
 * Initialize Google Analytics 4
 * Injects the gtag.js script into the page
 */
export const initGA4 = (): void => {
  const measurementId = import.meta.env.VITE_GA4_MEASUREMENT_ID;

  if (!measurementId || measurementId === 'your-measurement-id') {
    console.warn('GA4 not initialized: VITE_GA4_MEASUREMENT_ID not configured');
    return;
  }

  // Initialize dataLayer
  window.dataLayer = window.dataLayer || [];
  window.gtag = function() {
    window.dataLayer.push(arguments);
  };

  // Set initial timestamp
  window.gtag('js', new Date());

  // Configure GA4
  window.gtag('config', measurementId, {
    send_page_view: true
  });

  // Inject gtag.js script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;

  const firstScript = document.getElementsByTagName('script')[0];
  firstScript.parentNode?.insertBefore(script, firstScript);

  console.log(`GA4 initialized with Measurement ID: ${measurementId}`);
};

/**
 * Send a custom event to GA4
 *
 * @param eventName - Event name
 * @param eventParams - Event parameters
 */
export const trackEvent = (eventName: string, eventParams?: Record<string, any>): void => {
  if (!window.gtag) {
    console.warn('GA4 not initialized - gtag not available');
    return;
  }

  window.gtag('event', eventName, eventParams);
  console.log('GA4 Event:', eventName, eventParams);
};

/**
 * Track page view
 *
 * @param page - Page path or name
 * @param title - Page title
 */
export const trackPageView = (page: string, title?: string): void => {
  trackEvent('page_view', {
    page_path: page,
    page_title: title || document.title
  });
};

/**
 * Track user login
 *
 * @param userId - User ID
 * @param method - Login method (e.g., 'email', 'oauth')
 */
export const trackLogin = (userId: string, method: string = 'email'): void => {
  trackEvent('login', {
    user_id: userId,
    method
  });
};

/**
 * Track user registration
 *
 * @param userId - User ID
 */
export const trackSignUp = (userId: string): void => {
  trackEvent('sign_up', {
    user_id: userId
  });
};

/**
 * Track rate plan creation
 *
 * @param planId - Rate plan ID
 * @param planName - Rate plan name
 */
export const trackRatePlanCreated = (planId: number, planName: string): void => {
  trackEvent('rate_plan_created', {
    plan_id: planId,
    plan_name: planName
  });
};

/**
 * Track rate plan calculation
 *
 * @param retailPrice - Calculated retail price
 */
export const trackCalculation = (retailPrice: number): void => {
  trackEvent('price_calculated', {
    retail_price: retailPrice
  });
};

/**
 * Set user ID for tracking
 *
 * @param userId - User ID
 */
export const setUserId = (userId: string): void => {
  if (!window.gtag) {
    console.warn('GA4 not initialized - gtag not available');
    return;
  }

  window.gtag('set', 'user_id', userId);
};

/**
 * Set user properties
 *
 * @param properties - User properties to set
 */
export const setUserProperties = (properties: Record<string, any>): void => {
  if (!window.gtag) {
    console.warn('GA4 not initialized - gtag not available');
    return;
  }

  window.gtag('set', 'user_properties', properties);
};

/**
 * Track tab navigation
 *
 * @param tabName - Name of the tab being viewed
 */
export const trackTabView = (tabName: string): void => {
  trackEvent('tab_view', {
    tab_name: tabName
  });
};

/**
 * Track rate plan viewing
 *
 * @param planId - Rate plan ID
 * @param planName - Rate plan name
 */
export const trackRatePlanViewed = (planId: number, planName: string): void => {
  trackEvent('rate_plan_viewed', {
    plan_id: planId,
    plan_name: planName
  });
};

/**
 * Track rate plan deletion
 *
 * @param planId - Rate plan ID
 * @param planName - Rate plan name
 */
export const trackRatePlanDeleted = (planId: number, planName: string): void => {
  trackEvent('rate_plan_deleted', {
    plan_id: planId,
    plan_name: planName
  });
};

/**
 * Track rate plan update
 *
 * @param planId - Rate plan ID
 * @param planName - Rate plan name
 */
export const trackRatePlanUpdated = (planId: number, planName: string): void => {
  trackEvent('rate_plan_updated', {
    plan_id: planId,
    plan_name: planName
  });
};

/**
 * Track user creation (admin action)
 *
 * @param userId - New user ID
 * @param userRole - Role assigned to the user
 */
export const trackUserCreated = (userId: number, userRole: string): void => {
  trackEvent('user_created', {
    user_id: userId,
    user_role: userRole
  });
};

/**
 * Track user update (admin action)
 *
 * @param userId - User ID
 * @param userRole - Updated role
 */
export const trackUserUpdated = (userId: number, userRole: string): void => {
  trackEvent('user_updated', {
    user_id: userId,
    user_role: userRole
  });
};

/**
 * Track user deletion (admin action)
 *
 * @param userId - User ID
 */
export const trackUserDeleted = (userId: number): void => {
  trackEvent('user_deleted', {
    user_id: userId
  });
};
